import { useCallback, useState } from 'react';

import { ArrowLeftIcon, CircleCheckIcon, InfoIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router';

import { EditableText, EditableTextVariant } from '@/components/atoms/EditableText/EditableText';
import {
  PageSubHeader,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader/PageSubHeader';
import PageContentWrapper from '@/components/templates/PageContentWrapper';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { Form } from '@/shared/types/broadcast';
import { PageName } from '@/shared/types/segment';
import { capitalizeFirstLetter, getRestrictedNames } from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';
import { getEditableTextValidationSchema } from '@/util/validation/schema-builders';

import BroadcastLayout from '../components/BroadcastLayout/BroadcastLayout';
import BroadcastPageSidebar from '../components/BroadcastSidebar/BroadcastPageSidebar';
import { MessageFieldset } from '../components/MessageFieldset/MessageFieldset';
import { SettingsFieldset } from '../components/SettingsFieldset/SettingsFieldset';
import { UploadFieldset } from '../components/UploadFieldset/UploadFieldset';
import { useBroadcasts } from '../hooks/useBroadcasts';
import { useSubscribers } from '../hooks/useSubscribers';
import { actions } from '../redux/actions';
import { selectBroadcast, selectIsDraft, selectIsReadOnly } from '../redux/selectors';
import { SEND_NOW, isScheduledToBeSentNow, showBroadcastBanner } from '../utils';

export const Broadcast = ({ onSubmit, saveButtonLoading }) => {
  useSegments(PageName.BROADCAST);
  const { handleSubmit, control } = useFormContext<Form>();
  const template = useWatch({ control, name: 'template' });
  const scheduleDate = useWatch({ control, name: 'scheduleDate' });
  const dispatch = useDispatch();

  const [buttonClicked, setButtonClicked] = useState<number>(0);
  const { broadcastId } = useParams();
  const broadcast = useSelector(selectBroadcast);
  const isReadOnly = useSelector(selectIsReadOnly);
  const isDraft = useSelector(selectIsDraft);
  const { subscribers: APISubscribers, validSubscribersCount } = useSubscribers(broadcastId);
  const slug = useSelector(selectAccountSlug);
  const { flatBroadcasts } = useBroadcasts({ broadcastId });

  const { t } = useTranslation();

  const restrictedValues = getRestrictedNames(
    flatBroadcasts,
    flatBroadcasts?.find((b) => b.broadcast_id === broadcast.broadcast_id)?.name ?? ''
  );
  const validationSchema = getEditableTextValidationSchema(
    LENGTH_XS,
    restrictedValues,
    t('broadcasts.broadcast_name')
  );

  const handleEditNameSubmit = useCallback(
    (text: string) => {
      dispatch(actions.updateBroadcast({ name: text }));
    },
    [dispatch]
  );

  const editableTextProps = {
    defaultValue: broadcast?.name,
    onSubmit: handleEditNameSubmit,
    variant: 'body-semi-bold' as EditableTextVariant,
    canEdit: !isReadOnly,
    validationSchema,
  };

  const isScheduledButtonDisabled = useCallback(() => {
    if (isReadOnly || !template || !scheduleDate) {
      return true;
    }
    if (isDraft && validSubscribersCount === 0) {
      return true;
    }
    if (APISubscribers?.pages?.[0].subscribers?.length === 0) {
      return true;
    }
    return false;
  }, [APISubscribers?.pages, template, scheduleDate, isDraft, isReadOnly, validSubscribersCount]);

  const sheduledButtonText =
    scheduleDate === SEND_NOW ? t('chatBox.send') : t('business_hours.schedule');

  const computedStatus = isScheduledToBeSentNow(broadcast) ? 'in_progress' : broadcast?.status;

  const { show, bannerVariant, bannerTitle, bannerSubstitle } = showBroadcastBanner(
    computedStatus,
    broadcast,
    t
  );

  const handleButtonClick = (formData: Form | undefined, index: number) => {
    setButtonClicked(index);
    onSubmit(formData, index);
  };
  return (
    <>
      <PageSubHeader
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={`/${slug}/broadcasts`} className="breadcrumb-link">
                      <div className="flex items-center">
                        <div className="flex h-8 w-8 items-center justify-center">
                          <ArrowLeftIcon size={16} className="text-muted-foreground" />
                        </div>
                        <div className="mr-4 ml-2 h-4 w-px bg-border" />
                        {t('main_navigation.broadcast_other')}
                      </div>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="border-0 p-0">
                    <EditableText<typeof validationSchema> {...editableTextProps} />
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
      />
      <PageContentWrapper whiteBackground>
        <BroadcastLayout>
          <>
            <BroadcastLayout.Main>
              <div className="box-content max-w-xl px-8">
                {show && (
                  <div className="mb-10">
                    <Alert
                      variant={bannerVariant === 'success' ? 'default' : 'info'}
                      className={cn(
                        'gap-y-2',
                        bannerVariant === 'success'
                          ? 'border-success text-success'
                          : 'text-muted-foreground'
                      )}
                    >
                      {bannerVariant === 'success' ? (
                        <CircleCheckIcon size={16} />
                      ) : (
                        <InfoIcon size={16} />
                      )}
                      <AlertTitle className="line-clamp-none text-sm leading-5 font-semibold tracking-normal">
                        {bannerTitle}
                      </AlertTitle>
                      {bannerSubstitle && <AlertDescription>{bannerSubstitle}</AlertDescription>}
                    </Alert>
                  </div>
                )}

                <UploadFieldset isReadOnly={isReadOnly} />
                <Separator className="my-6" />
                <MessageFieldset isReadOnly={isReadOnly} />
                <Separator className="my-6" />
                <SettingsFieldset isReadOnly={isReadOnly} />
                <div className="mt-8 flex gap-4">
                  <Button
                    variant="outline-neutral"
                    onClick={() => handleButtonClick(undefined, 0)}
                    isLoading={saveButtonLoading && buttonClicked === 0}
                    disabled={isReadOnly}
                  >
                    {t('broadcasts.save_draft')}
                  </Button>
                  <Button
                    onClick={handleSubmit((formData) => handleButtonClick(formData, 1))}
                    isLoading={saveButtonLoading && buttonClicked === 1}
                    disabled={isScheduledButtonDisabled()}
                  >
                    {capitalizeFirstLetter(sheduledButtonText)}
                  </Button>
                </div>
              </div>
            </BroadcastLayout.Main>

            <BroadcastLayout.Sidebar>
              <BroadcastPageSidebar />
            </BroadcastLayout.Sidebar>
          </>
        </BroadcastLayout>
      </PageContentWrapper>
    </>
  );
};
